import { BitReader } from '../aac/bitReader';
import type { Fraction } from '../util/fractionType';
export const slice_layer_without_partitioning_rbsp_idr = 0x05;
export const seq_parameter_set_rbsp = 0x07;
export const access_unit_delimiter_rbsp = 0x09;

export interface H264FrameInfo {
  profile: number;
  level: number;
  profileCompatibility: number;
  progressive: boolean;
  width: number;
  height: number;
  sampleAspectRatio: Fraction;
}

/**
 * The h264 NAL units are escaped to prevent the appearance of the following
 * byte sequences:
 *
 *   0x000000
 *   0x000001
 *   0x000002
 *   0x000003
 *
 * These sequences are respectively encoded with:
 *
 *   0x00000300
 *   0x00000301
 *   0x00000302
 *   0x00000303
 *
 * This function returns an array containing the unescaped data.
 */
export function decodeRBSP(escaped: Uint8Array): Uint8Array {
  const rbsp = new Uint8Array(escaped.byteLength);

  const len = escaped.byteLength;
  let src = 0;
  let dst = 0;

  while (src < len) {
    if (escaped[src] === 0 && escaped[src + 1] === 0 && escaped[src + 2] === 3) {
      rbsp[dst++] = 0;
      rbsp[dst++] = 0;
      rbsp[dst++] = src + 3;
      src += 4;
    } else {
      rbsp[dst++] = escaped[src++];
    }
  }

  return rbsp.subarray(0, dst);
}


function isProfileWithOptionalSpsData(profileIdc: number) {
  switch (profileIdc) {
    case 100:
    case 110:
    case 122:
    case 244:
    case 44:
    case 83:
    case 86:
    case 118:
    case 128:
    case 138:
    case 139:
    case 134: return true;
    default: return false;
  }
}


/**
 * Skips a scaling list.
 */
function skipScalingList(count: number, bitReader: BitReader) {
  let lastScale = 8;
  let nextScale = 8;

  for (let j = 0; j < count; j++) {
    if (nextScale !== 0) {
      const deltaScale = bitReader.readSignedExpGolomb();
      nextScale = (lastScale + deltaScale + 256) % 256;
    }

    lastScale = (nextScale === 0) ? lastScale : nextScale;
  }
}


/**
 * A sequence parameter set NAL containing information about the video
 * format (profile, dimensions, ...) encoded with exponential Golomb codes.
 * Since we only need the width and height, we parse what we need to obtain
 * this information.
 */
export function parseSequenceparameterSet(rbsp: Uint8Array): H264FrameInfo {
  const bitReader = new BitReader(rbsp);

  const profileIdc = bitReader.readBits(8);
  const profileCompatibility = bitReader.readBits(8);
  const levelIdc = bitReader.readBits(8);
  bitReader.readUnsignedExpGolomb(); // seq_parameter_set_id

  if (isProfileWithOptionalSpsData(profileIdc)) {
    const chromaFormatIdc = bitReader.readUnsignedExpGolomb();
    if (chromaFormatIdc === 3) {
      bitReader.readBits(1); // separate_colour_plane_flag
    }
    bitReader.readUnsignedExpGolomb(); // bit_depth_luma_minus8
    bitReader.readUnsignedExpGolomb(); // bit_depth_chroma_minus8
    bitReader.readBits(1); // qpprime_y_zero_transform_bypass_flag

    if (bitReader.readBits(1) === 1) { // seq_scaling_matrix_present_flag
      const scalingListCount = (chromaFormatIdc !== 3) ? 8 : 12;
      for (let i = 0; i < scalingListCount; i++) {
        if (bitReader.readBits(1) === 1) { // seq_scaling_list_present_flag[ i ]
          if (i < 6) {
            skipScalingList(16, bitReader);
          } else {
            skipScalingList(64, bitReader);
          }
        }
      }
    }
  }

  bitReader.readUnsignedExpGolomb(); // log2_max_frame_num_minus4
  const picOrderCntType = bitReader.readUnsignedExpGolomb();

  if (picOrderCntType === 0) {
    bitReader.readUnsignedExpGolomb(); // log2_max_pic_order_cnt_lsb_minus4
  } else if (picOrderCntType === 1) {
    bitReader.readBits(1); // delta_pic_order_always_zero_flag
    bitReader.readSignedExpGolomb(); // offset_for_non_ref_pic
    bitReader.readSignedExpGolomb(); // offset_for_top_to_bottom_field
    const numRefFramesInPicOrderCntCycle = bitReader.readUnsignedExpGolomb();
    for (let i = 0; i < numRefFramesInPicOrderCntCycle; i++) {
      bitReader.readSignedExpGolomb(); // offset_for_ref_frame[ i ]
    }
  }

  bitReader.readUnsignedExpGolomb(); // max_num_ref_frames
  bitReader.readBits(1); // gaps_in_frame_num_value_allowed_flag

  const picWidthInMbsMinus1 = bitReader.readUnsignedExpGolomb();
  const picHeightInMapUnitsMinus1 = bitReader.readUnsignedExpGolomb();

  const frameMbsOnlyFlag = bitReader.readBits(1);
  const progressive = frameMbsOnlyFlag === 1;
  if (frameMbsOnlyFlag === 0) {
    bitReader.readBits(1); // mb_adaptive_frame_field_flag
  }

  bitReader.readBits(1); // direct_8x8_inference_flag
  let frameCropLeftOffset = 0;
  let frameCropRightOffset = 0;
  let frameCropTopOffset = 0;
  let frameCropBottomOffset = 0;
  if (bitReader.readBits(1) === 1) { // frame_cropping_flag
    frameCropLeftOffset = bitReader.readUnsignedExpGolomb();
    frameCropRightOffset = bitReader.readUnsignedExpGolomb();
    frameCropTopOffset = bitReader.readUnsignedExpGolomb();
    frameCropBottomOffset = bitReader.readUnsignedExpGolomb();
  }

  let sarRatio = [1, 1];
  if (bitReader.readBits(1) === 1) {
    // vui_parameters_present_flag
    if (bitReader.readBits(1) === 1) {
      // aspect_ratio_info_present_flag
      const aspectRatioIdc = bitReader.readBits(8);
      switch (aspectRatioIdc) {
        case 1: sarRatio = [1, 1]; break;
        case 2: sarRatio = [12, 11]; break;
        case 3: sarRatio = [10, 11]; break;
        case 4: sarRatio = [16, 11]; break;
        case 5: sarRatio = [40, 33]; break;
        case 6: sarRatio = [24, 11]; break;
        case 7: sarRatio = [20, 11]; break;
        case 8: sarRatio = [32, 11]; break;
        case 9: sarRatio = [80, 33]; break;
        case 10: sarRatio = [18, 11]; break;
        case 11: sarRatio = [15, 11]; break;
        case 12: sarRatio = [64, 33]; break;
        case 13: sarRatio = [160, 99]; break;
        case 14: sarRatio = [4, 3]; break;
        case 15: sarRatio = [3, 2]; break;
        case 16: sarRatio = [2, 1]; break;
        case 255: {
          sarRatio = [bitReader.readBits(16), bitReader.readBits(16)];
          break;
        }
      }
    }
  }

  return {
    profile: profileIdc,
    level: levelIdc,
    profileCompatibility,
    progressive,
    width: (((picWidthInMbsMinus1 + 1) * 16) - frameCropLeftOffset * 2 - frameCropRightOffset * 2),
    height: (2 - frameMbsOnlyFlag) * (((picHeightInMapUnitsMinus1 + 1) * 16) - (frameCropTopOffset * 2) - (frameCropBottomOffset * 2)),
    sampleAspectRatio: {
      numerator: sarRatio[0],
      denominator: sarRatio[1],
    },
  };
}


export function getCodecString(info: H264FrameInfo): string {
  const profile = info.profile.toString(16).padStart(2, '0');
  const level = info.level.toString(16).padStart(2, '0');
  const compat = info.profileCompatibility.toString(16).padStart(2, '0');
  return `avc1.${profile}${compat}${level}`;

}
